import { useState, useRef, useEffect, useMemo, useCallback, createContext, useContext } from "react";

/* ═══ CONSTANTS ═══ */
const SUITS = ["s","h","d","c"];
const RANKS = ["A","K","Q","J","T","9","8","7","6","5","4","3","2"];
const SS = { s:"♠", h:"♥", d:"♦", c:"♣" };
const SC = { s:"#1a1a2e", h:"#c0392b", d:"#2980b9", c:"#27ae60" };
const SBG = { s:"#e8e8f0", h:"#fde8e8", d:"#e8f0fd", c:"#e8fde8" };
const RV = { A:14,K:13,Q:12,J:11,T:10,9:9,8:8,7:7,6:6,5:5,4:4,3:3,2:2 };
const VR = {}; Object.entries(RV).forEach(([k,v]) => VR[v] = k);
const PC = ["#e74c3c","#3498db","#2ecc71","#f39c12","#9b59b6","#1abc9c"];

function buildDeck() { const d=[]; for(const r of RANKS) for(const s of SUITS) d.push(r+s); return d; }
const FD = buildDeck();
function shuf(a) { const b=[...a]; for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];} return b; }

/* ═══ I18N SYSTEM ═══ */
const translations = {
  ko: {
    header: { title: "PLO Equity Calculator", subtitle: "POT LIMIT OMAHA 4 · 5 · 6" },
    scenario: {
      title: "시나리오 빌더", preset: "프리셋", custom: "커스텀",
      apply: "시나리오 적용", close: "닫기", presetAndCustom: "프리셋 & 커스텀",
      generating: "생성 중...", generate: "시나리오 생성", reset: "초기화",
      failRetry: "생성 실패. 다시 시도해주세요.", failHard: "이 조합은 생성이 어렵습니다.",
      selectAtLeast: "각 플레이어마다 최소 1개 선택", unselected: "미선택",
    },
    settings: { gameType: "게임 타입", players: "플레이어", simulation: "시뮬레이션", preflop: "프리플랍" },
    board: {
      title: "보드", auto: "자동", inputPlaceholder: "예: AhKdQc",
      input: "입력", select: "선택", close: "닫기",
      flop: "플랍", turn: "턴", river: "리버", clear: "✕",
    },
    player: {
      title: "Player", inputPlaceholder4: "예: AsKhQdJc", inputPlaceholder5: "예: AsKhQdJcTh", inputPlaceholder6: "예: AsKhQdJcTh9s",
      input: "입력", select: "선택", close: "닫기", clickToRemove: "클릭하여 제거",
    },
    results: {
      title: "결과", exact: "정확한 계산", win: "승리", tie: "타이", equity: "에퀴티",
      totalExact: (n) => `전체 ${n}가지 · 정확한 계산`,
      totalMC: (n) => `시뮬레이션 ${n}회 · Monte Carlo`,
    },
    calc: {
      calculating: "계산 중...", exactCalc: "정확한 경우의 수 계산 중...", mcCalc: "Monte Carlo 시뮬레이션 중...",
      recalc: "재계산", calcEquity: "에퀴티 계산", clearAll: "전체 초기화",
    },
    nextCard: {
      title: (label) => `다음 ${label} 카드별 에퀴티`,
      chart: "차트", grid: "그리드",
      sortBy: "정렬 기준", suit: "무늬", all: "전체", cards: "장",
      best: "최고", worst: "최저", avg: "평균", favorable: "유리",
    },
    usage: {
      title: "사용법",
      scenario: "시나리오:", scenarioDesc: "프리셋 선택 또는 커스텀 조합으로 자동 딜",
      input: "입력:", inputDesc: "AsKhQdJc 형식 텍스트 또는 카드 선택 UI",
      autoCalc: "자동 계산:", autoCalcDesc: "보드 3장+ & 핸드 완성 시 에퀴티 + 다음 카드 분석 자동 실행",
      cardClick: "카드 클릭:", cardClickDesc: "턴/리버 에퀴티 리스트에서 카드 클릭하면 보드에 자동 추가",
      flopTurnRiver: "플랍/턴/리버:", exactDesc: "정확한 전수 계산",
      preflopLabel: "프리플랍:", mcDesc: "Monte Carlo",
      notation: "표기:", notationDesc: "A K Q J T=10 · s=♠ h=♥ d=♦ c=♣",
    },
    emptyState: "플레이어 핸드와 보드를 설정하면\n에퀴티 계산 결과가 여기에 표시됩니다",
    suitNames: { s: "스페이드", h: "하트", d: "다이아", c: "클로버" },
    categories: { madeHands: "메이드 핸드", draws: "드로우", others: "기타" },
    hands: {
      top_pair: "탑페어", middle_pair: "미들페어", bottom_pair: "바텀페어", overpair: "오버페어",
      trips: "트립스", top_set: "탑셋", middle_set: "미들셋", bottom_set: "바텀셋",
      set: "셋", two_pair: "투페어", straight: "스트레이트",
      flush: "플러시", full_house: "풀하우스", quads: "쿼드", nut_flush: "넛플러시",
      oesd: "OESD", gutshot: "거터", flush_draw: "FD", nut_flush_draw: "NFD",
      wrap: "랩", combo_draw: "콤보드로우",
      overcards: "오버카드", backdoor_sd: "백도어 SD", backdoor_fd: "백도어 FD",
      blocker: "NF 블로커", air: "Air",
    },
  },
  en: {
    header: { title: "PLO Equity Calculator", subtitle: "POT LIMIT OMAHA 4 · 5 · 6" },
    scenario: {
      title: "Scenario Builder", preset: "Preset", custom: "Custom",
      apply: "Apply Scenario", close: "Close", presetAndCustom: "Preset & Custom",
      generating: "Generating...", generate: "Generate Scenario", reset: "Reset",
      failRetry: "Generation failed. Please try again.", failHard: "This combination is difficult to generate.",
      selectAtLeast: "Select at least 1 for each player", unselected: "None",
    },
    settings: { gameType: "Game Type", players: "Players", simulation: "Simulation", preflop: "Preflop" },
    board: {
      title: "Board", auto: "Auto", inputPlaceholder: "e.g. AhKdQc",
      input: "Input", select: "Select", close: "Close",
      flop: "Flop", turn: "Turn", river: "River", clear: "✕",
    },
    player: {
      title: "Player", inputPlaceholder4: "e.g. AsKhQdJc", inputPlaceholder5: "e.g. AsKhQdJcTh", inputPlaceholder6: "e.g. AsKhQdJcTh9s",
      input: "Input", select: "Select", close: "Close", clickToRemove: "Click to remove",
    },
    results: {
      title: "Results", exact: "Exact Calculation", win: "Win", tie: "Tie", equity: "Equity",
      totalExact: (n) => `Total ${n} cases · Exact calculation`,
      totalMC: (n) => `${n} simulations · Monte Carlo`,
    },
    calc: {
      calculating: "Calculating...", exactCalc: "Calculating exact cases...", mcCalc: "Running Monte Carlo simulation...",
      recalc: "Recalculate", calcEquity: "Calculate Equity", clearAll: "Clear All",
    },
    nextCard: {
      title: (label) => `Next ${label} Card Equity`,
      chart: "Chart", grid: "Grid",
      sortBy: "Sort by", suit: "Suit", all: "All", cards: "cards",
      best: "Best", worst: "Worst", avg: "Avg", favorable: "Favorable",
    },
    usage: {
      title: "Usage",
      scenario: "Scenario:", scenarioDesc: "Auto-deal with preset or custom combinations",
      input: "Input:", inputDesc: "Text format AsKhQdJc or card picker UI",
      autoCalc: "Auto Calc:", autoCalcDesc: "Auto equity + next card analysis when board 3+ & hands complete",
      cardClick: "Card Click:", cardClickDesc: "Click cards in turn/river equity list to add to board",
      flopTurnRiver: "Flop/Turn/River:", exactDesc: "Exact enumeration",
      preflopLabel: "Preflop:", mcDesc: "Monte Carlo",
      notation: "Notation:", notationDesc: "A K Q J T=10 · s=♠ h=♥ d=♦ c=♣",
    },
    emptyState: "Set player hands and board to see\nequity calculation results here",
    suitNames: { s: "Spades", h: "Hearts", d: "Diamonds", c: "Clubs" },
    categories: { madeHands: "Made Hands", draws: "Draws", others: "Others" },
    hands: {
      top_pair: "Top Pair", middle_pair: "Middle Pair", bottom_pair: "Bottom Pair", overpair: "Overpair",
      trips: "Trips", top_set: "Top Set", middle_set: "Middle Set", bottom_set: "Bottom Set",
      set: "Set", two_pair: "Two Pair", straight: "Straight",
      flush: "Flush", full_house: "Full House", quads: "Quads", nut_flush: "Nut Flush",
      oesd: "OESD", gutshot: "Gutshot", flush_draw: "FD", nut_flush_draw: "NFD",
      wrap: "Wrap", combo_draw: "Combo Draw",
      overcards: "Overcards", backdoor_sd: "Backdoor SD", backdoor_fd: "Backdoor FD",
      blocker: "NF Blocker", air: "Air",
    },
  },
};

const LangContext = createContext("ko");
function useT() {
  const lang = useContext(LangContext);
  return translations[lang];
}

/* ═══ POKER LOGIC ═══ */
function combo(a,k) {
  if(k===0) return [[]]; if(a.length<k) return [];
  const r=[]; for(let i=0;i<=a.length-k;i++){const rest=combo(a.slice(i+1),k-1);for(const c of rest) r.push([a[i],...c]);}
  return r;
}

function ev5(cards) {
  const vals=cards.map(c=>RV[c[0]]).sort((a,b)=>b-a), suits=cards.map(c=>c[1]);
  const fl=suits.every(s=>s===suits[0]);
  let st=false,sh=0; const u=[...new Set(vals)].sort((a,b)=>b-a);
  if(u.length>=5){for(let i=0;i<=u.length-5;i++)if(u[i]-u[i+4]===4){st=true;sh=u[i];break;}
  if(!st&&u.includes(14)&&u.includes(5)&&u.includes(4)&&u.includes(3)&&u.includes(2)){st=true;sh=5;}}
  const cn={}; for(const v of vals) cn[v]=(cn[v]||0)+1;
  const g=Object.entries(cn).map(([v,c])=>({v:+v,c})).sort((a,b)=>b.c-a.c||b.v-a.v);
  const p=g.map(x=>x.c).join("");
  if(fl&&st) return{r:8,h:sh,k:[]}; if(p[0]==="4") return{r:7,h:g[0].v,k:[g[1].v]};
  if(p==="32") return{r:6,h:g[0].v,k:[g[1].v]}; if(fl) return{r:5,h:0,k:vals};
  if(st) return{r:4,h:sh,k:[]}; if(p[0]==="3") return{r:3,h:g[0].v,k:g.slice(1).map(x=>x.v)};
  if(p.startsWith("22")) return{r:2,h:g[0].v,k:[g[1].v,g[2].v]};
  if(p[0]==="2") return{r:1,h:g[0].v,k:g.slice(1).map(x=>x.v)};
  return{r:0,h:0,k:vals};
}

function hcmp(a,b) {
  if(a.r!==b.r) return a.r-b.r; if(a.h!==b.h) return a.h-b.h;
  for(let i=0;i<Math.min(a.k.length,b.k.length);i++) if(a.k[i]!==b.k[i]) return a.k[i]-b.k[i];
  return 0;
}

function bestO(hole,board) {
  const hc=combo(hole,2),bc=combo(board,3); let best=null;
  for(const h of hc) for(const b of bc){const e=ev5([...h,...b]);if(!best||hcmp(e,best)>0)best=e;}
  return best;
}

function evBoard(players,board) {
  const hands=players.map(p=>bestO(p,board)); let bi=0,ti=[0];
  for(let i=1;i<hands.length;i++){const c=hcmp(hands[i],hands[bi]);if(c>0){bi=i;ti=[i];}else if(c===0)ti.push(i);}
  return{bi,ti};
}

function runCalc(players,board,simCount) {
  const used=new Set(); for(const p of players) for(const c of p) used.add(c); for(const c of board) used.add(c);
  const rem=FD.filter(c=>!used.has(c)), need=5-board.length;
  const wins=new Array(players.length).fill(0), ties=new Array(players.length).fill(0); let total=0;
  if(need===0){const{bi,ti}=evBoard(players,board);if(ti.length===1)wins[bi]++;else for(const i of ti)ties[i]++;total=1;}
  else if(need===1){for(let i=0;i<rem.length;i++){const{bi,ti}=evBoard(players,[...board,rem[i]]);if(ti.length===1)wins[bi]++;else for(const x of ti)ties[x]++;total++;}}
  else if(need===2){for(let i=0;i<rem.length-1;i++)for(let j=i+1;j<rem.length;j++){const{bi,ti}=evBoard(players,[...board,rem[i],rem[j]]);if(ti.length===1)wins[bi]++;else for(const x of ti)ties[x]++;total++;}}
  else{for(let s=0;s<simCount;s++){const sh=shuf(rem);const{bi,ti}=evBoard(players,[...board,...sh.slice(0,need)]);if(ti.length===1)wins[bi]++;else for(const x of ti)ties[x]++;total++;}}
  const method=need<=2?"exact":"montecarlo";
  return players.map((_,i)=>({win:((wins[i]/total)*100).toFixed(2),tie:((ties[i]/total)*100).toFixed(2),equity:(((wins[i]+ties[i]/2)/total)*100).toFixed(2),total,method}));
}

/* ═══ NEXT-CARD ANALYSIS ═══ */
function calcNextCardEquities(players, board) {
  const used = new Set();
  for (const p of players) for (const c of p) used.add(c);
  for (const c of board) used.add(c);
  const remaining = FD.filter(c => !used.has(c));
  const results = [];

  for (const card of remaining) {
    const newBoard = [...board, card];
    const need = 5 - newBoard.length;
    const rem2 = remaining.filter(c => c !== card);
    const wins = new Array(players.length).fill(0);
    const ties = new Array(players.length).fill(0);
    let total = 0;

    if (need === 0) {
      const { bi, ti } = evBoard(players, newBoard);
      if (ti.length === 1) wins[bi]++; else for (const x of ti) ties[x]++;
      total = 1;
    } else if (need === 1) {
      for (let i = 0; i < rem2.length; i++) {
        const { bi, ti } = evBoard(players, [...newBoard, rem2[i]]);
        if (ti.length === 1) wins[bi]++; else for (const x of ti) ties[x]++;
        total++;
      }
    } else if (need === 2) {
      for (let i = 0; i < rem2.length - 1; i++) {
        for (let j = i + 1; j < rem2.length; j++) {
          const { bi, ti } = evBoard(players, [...newBoard, rem2[i], rem2[j]]);
          if (ti.length === 1) wins[bi]++; else for (const x of ti) ties[x]++;
          total++;
        }
      }
    }

    const equities = players.map((_, i) => +((wins[i] + ties[i] / 2) / total * 100).toFixed(1));
    results.push({ card, equities });
  }

  return results;
}

/* ═══ HAND ANALYSIS ═══ */
function anyStraight(hole, board) {
  const hc=combo(hole,2),bc=combo(board,3);
  for(const h of hc) for(const b of bc){
    const vals=[...h,...b].map(c=>RV[c[0]]);
    const u=[...new Set(vals)].sort((a,b)=>b-a);
    if(u.length>=5){for(let i=0;i<=u.length-5;i++)if(u[i]-u[i+4]===4)return true;
    if(u.includes(14)&&u.includes(5)&&u.includes(4)&&u.includes(3)&&u.includes(2))return true;}
  }
  return false;
}

function analyzeHand(playerCards, boardCards, lang) {
  if(!playerCards||playerCards.length<2||!boardCards||boardCards.length<3) return '';
  const t=translations[lang]; const parts=[]; const best=bestO(playerCards,boardCards);
  if(!best) return '';
  const boardVals=boardCards.map(c=>RV[c[0]]).sort((a,b)=>b-a);

  // Made hand
  if(best.r>=7) parts.push(t.hands.quads);
  else if(best.r===6) parts.push(t.hands.full_house);
  else if(best.r===5){
    let isNut=false;
    for(const suit of SUITS){const hs=playerCards.filter(c=>c[1]===suit);const bs=boardCards.filter(c=>c[1]===suit);
    if(hs.length>=2&&bs.length>=3&&hs.some(c=>c[0]==='A')){isNut=true;break;}}
    parts.push(isNut?t.hands.nut_flush:t.hands.flush);
  }
  else if(best.r===4) parts.push(t.hands.straight);
  else if(best.r===3){
    const tv=best.h,pih=playerCards.filter(c=>RV[c[0]]===tv).length>=2;
    if(pih){if(tv===boardVals[0])parts.push(t.hands.top_set);else if(tv===boardVals[Math.min(1,boardVals.length-1)])parts.push(t.hands.middle_set);else parts.push(t.hands.bottom_set);}
    else parts.push(t.hands.trips);
  }
  else if(best.r===2) parts.push(t.hands.two_pair);
  else if(best.r===1){
    const pv=best.h;
    if(pv>boardVals[0])parts.push(t.hands.overpair);
    else if(pv===boardVals[0])parts.push(t.hands.top_pair);
    else if(boardVals.length>1&&pv===boardVals[1])parts.push(t.hands.middle_pair);
    else if(boardVals.includes(pv))parts.push(t.hands.bottom_pair);
  }

  // Draws (flop/turn only) - only show draws that produce a HIGHER hand
  if(boardCards.length<=4){
    // Flush draw: only if current best < flush (r<5)
    if(best.r<5){
      for(const suit of SUITS){
        const hs=playerCards.filter(c=>c[1]===suit).length;
        const bs=boardCards.filter(c=>c[1]===suit).length;
        if(hs>=2&&bs===2){
          parts.push(playerCards.some(c=>c[0]==='A'&&c[1]===suit)?t.hands.nut_flush_draw:t.hands.flush_draw);
          break;
        }
      }
    }
    // Straight draw: only if current best < straight (r<4)
    if(best.r<4){
      const used=new Set([...playerCards,...boardCards]);
      const rem=FD.filter(c=>!used.has(c));
      let outs=0;
      for(const card of rem){const nb=[...boardCards,card];if(nb.length>5)continue;if(anyStraight(playerCards,nb))outs++;}
      if(outs>=9)parts.push(t.hands.wrap);
      else if(outs>=6)parts.push(t.hands.oesd);
      else if(outs>=3)parts.push(t.hands.gutshot);
    }
  }

  return parts.join('+');
}

function parseText(text) {
  const t=text.trim().toUpperCase().replace(/10/g,"T"); const cards=[]; let i=0;
  while(i<t.length){if(i+1<t.length){const r=t[i],s=t[i+1].toLowerCase();if(RANKS.includes(r)&&SUITS.includes(s)){cards.push(r+s);i+=2;continue;}}i++;}
  return cards;
}

/* ═══ SCENARIO ENGINE ═══ */
const MADE_HANDS = [
  {id:"top_pair",cat:"pair"},{id:"middle_pair",cat:"pair"},{id:"bottom_pair",cat:"pair"},{id:"overpair",cat:"pair"},
  {id:"trips",cat:"trips"},
  {id:"top_set",cat:"set"},{id:"middle_set",cat:"set"},{id:"bottom_set",cat:"set"},
  {id:"two_pair",cat:"twopair"},
  {id:"straight",cat:"straight"},
  {id:"flush",cat:"flush"},
  {id:"full_house",cat:"fullhouse"},
  {id:"quads",cat:"quads"},
  {id:"nut_flush",cat:"nutflush"},
];
const DRAWS = [
  {id:"oesd"},{id:"gutshot"},{id:"flush_draw"},{id:"nut_flush_draw"},
  {id:"wrap"},{id:"combo_draw"},
];
const EXTRAS = [
  {id:"overcards"},{id:"backdoor_sd"},{id:"backdoor_fd"},{id:"blocker"},{id:"air"},
];
const ALL_OPTIONS = [...MADE_HANDS,...DRAWS,...EXTRAS];

/* Hand strength ranking for hierarchy (higher = stronger, follows poker rank) */
const MADE_STRENGTH = {
  bottom_pair:1, middle_pair:1, top_pair:1, overpair:1,
  two_pair:2,
  trips:3, bottom_set:3, middle_set:3, top_set:3,
  straight:4,
  flush:5, nut_flush:5,
  full_house:6,
  quads:7,
};
/* What rank a draw produces if completed */
const DRAW_PRODUCES = {
  gutshot:4, oesd:4, wrap:4,
  flush_draw:5, nut_flush_draw:5,
  combo_draw:5,
};
const MADE_IDS = new Set(Object.keys(MADE_STRENGTH));
const DRAW_IDS = new Set(Object.keys(DRAW_PRODUCES));

const PRESETS = [
  {id:"set_vs_nfd",p1:["top_set"],p2:["nut_flush_draw"]},
  {id:"2p_vs_fd_overpair",p1:["two_pair"],p2:["flush_draw","overpair"]},
  {id:"nfd_vs_pair_gut",p1:["nut_flush_draw"],p2:["top_pair","gutshot"]},
  {id:"topset_vs_wrap",p1:["top_set"],p2:["wrap"]},
  {id:"topset_vs_combo",p1:["top_set"],p2:["combo_draw"]},
  {id:"overpair_vs_nfd_oesd",p1:["overpair"],p2:["nut_flush_draw","oesd"]},
  {id:"2p_vs_nfd",p1:["two_pair"],p2:["nut_flush_draw"]},
  {id:"straight_vs_set_fd",p1:["straight"],p2:["top_set","flush_draw"]},
];

const PRESET_NAMES = {
  ko: {
    set_vs_nfd: "탑셋 vs NFD",
    "2p_vs_fd_overpair": "투페어 vs FD+오버페어",
    nfd_vs_pair_gut: "NFD vs 탑페어+거터",
    topset_vs_wrap: "탑셋 vs 랩",
    topset_vs_combo: "탑셋 vs 콤보드로우",
    overpair_vs_nfd_oesd: "오버페어 vs NFD+OESD",
    "2p_vs_nfd": "투페어 vs NFD",
    straight_vs_set_fd: "스트레이트 vs 탑셋+FD",
  },
  en: {
    set_vs_nfd: "Top Set vs NFD",
    "2p_vs_fd_overpair": "Two Pair vs FD+Overpair",
    nfd_vs_pair_gut: "NFD vs Top Pair+Gutshot",
    topset_vs_wrap: "Top Set vs Wrap",
    topset_vs_combo: "Top Set vs Combo Draw",
    overpair_vs_nfd_oesd: "Overpair vs NFD+OESD",
    "2p_vs_nfd": "Two Pair vs NFD",
    straight_vs_set_fd: "Straight vs Top Set+FD",
  },
};

function generateScenario(p1o,p2o,gt=4) {
  for(let a=0;a<200;a++){const r=tryGen(p1o,p2o,gt);if(r)return r;} return null;
}

function tryGen(p1o,p2o,gt) {
  const as=shuf([...SUITS]),fs=as[0],ns=as[1],ts=as[2];
  const allOpts = [...p1o,...p2o];
  const needsFlush = allOpts.some(o=>["flush","nut_flush","flush_draw","nut_flush_draw","combo_draw"].includes(o));
  const needsPaired = allOpts.some(o=>["trips","full_house"].includes(o));
  const needsConn = allOpts.some(o=>["straight","oesd","wrap","gutshot","combo_draw"].includes(o));
  const mid=[7,8,9,10,11],hi=[10,11,12,13];
  let br;
  if(needsConn){
    const sv=mid[Math.floor(Math.random()*mid.length)];
    br=(allOpts.includes("straight"))?[sv,sv-1,sv-2]:[sv,sv-1,sv-3];
  } else {
    const pool=shuf([...hi,...mid]);
    br=[pool[0],pool[1],pool[2]];
  }
  br=[...new Set(br)].slice(0,3);
  while(br.length<3){const r=Math.floor(Math.random()*9)+4;if(!br.includes(r))br.push(r);}
  br.sort((a,b)=>b-a);
  if(needsPaired) br[2]=br[0];
  let bs;
  if(needsFlush){
    const madeFlush = allOpts.some(o=>["flush","nut_flush"].includes(o));
    bs=madeFlush?[fs,fs,fs]:[fs,fs,ns];
  } else {
    bs=shuf([fs,ns,ts]);
  }
  const board=br.map((r,i)=>VR[r]+bs[i]);
  if(new Set(board).size!==3) return null;
  const used=new Set(board);
  const p1c=genHole(p1o,board,br,bs,fs,used,gt); if(!p1c) return null;
  for(const c of p1c) used.add(c);
  const p2c=genHole(p2o,board,br,bs,fs,used,gt); if(!p2c) return null;

  // Validate: check that generated hands match requested scenarios exactly
  if (!validateScenario(p1o, p1c, board, br, bs, fs) || !validateScenario(p2o, p2c, board, br, bs, fs)) {
    return null;
  }

  return{board,players:[p1c,p2c]};
}

function validateScenario(opts, holeCards, board, br, bs, fs) {
  const holeVals = holeCards.map(c => RV[c[0]]);
  const holeSuits = holeCards.map(c => c[1]);
  const boardVals = board.map(c => RV[c[0]]);

  for (const opt of opts) {
    switch(opt) {
      case "flush_draw": {
        // Must have exactly FD (2 cards of flush suit), NOT NFD (no Ace of flush suit)
        const flushSuitOnBoard = bs.filter(s => s === fs).length;
        if (flushSuitOnBoard < 2) return false;
        const holeFsCards = holeCards.filter(c => c[1] === fs);
        if (holeFsCards.length < 1) return false;
        // Must NOT have Ace of flush suit (that would be NFD)
        if (holeFsCards.some(c => c[0] === "A")) return false;
        break;
      }
      case "nut_flush_draw": {
        const flushSuitOnBoard = bs.filter(s => s === fs).length;
        if (flushSuitOnBoard < 2) return false;
        // Must have Ace of flush suit
        if (!holeCards.some(c => c[1] === fs && c[0] === "A")) return false;
        // Board must not already be 3 of same suit (that would be made flush, not draw)
        if (flushSuitOnBoard >= 3) return false;
        break;
      }
      case "top_pair": {
        // Must pair top board card, must NOT have two pair or set
        const topCard = br[0];
        if (!holeVals.includes(topCard)) return false;
        // Must not have a set (two hole cards matching same board card)
        if (holeVals.filter(v => v === topCard).length >= 2) return false;
        // Must not pair another board card (would be two pair)
        if (holeVals.some(v => v !== topCard && boardVals.includes(v))) return false;
        break;
      }
      case "two_pair": {
        // Must pair exactly two different board cards
        const paired = boardVals.filter(v => holeVals.includes(v));
        const uniquePaired = [...new Set(paired)];
        if (uniquePaired.length < 2) return false;
        break;
      }
      case "set":
      case "top_set":
      case "middle_set":
      case "bottom_set": {
        // Must have pocket pair matching a board card
        const targetIdx = opt === "top_set" ? 0 : opt === "middle_set" ? 1 : opt === "bottom_set" ? 2 : 0;
        const targetVal = br[targetIdx];
        if (holeVals.filter(v => v === targetVal).length < 2) return false;
        break;
      }
      case "straight": {
        // Check that the 5-card combination actually makes a straight
        const best = bestO(holeCards, board);
        if (!best || best.r !== 4) return false; // r=4 is straight
        // Must NOT be a flush
        if (best.r === 5 || best.r === 8) return false;
        break;
      }
      case "flush": {
        const best = bestO(holeCards, board);
        if (!best || best.r !== 5) return false;
        // Must NOT be nut flush (Ace-high flush)
        const holeFsCards = holeCards.filter(c => c[1] === fs);
        if (holeFsCards.some(c => c[0] === "A")) return false;
        break;
      }
      case "nut_flush": {
        const best = bestO(holeCards, board);
        if (!best || best.r < 5) return false;
        // Must have Ace of flush suit
        if (!holeCards.some(c => c[1] === fs && c[0] === "A")) return false;
        break;
      }
      case "wrap": {
        const best = bestO(holeCards, board);
        if (best && best.r >= 4) return false;
        break;
      }
    }
  }
  return true;
}

function genHole(opts,board,br,bs,fs,used,gt) {
  const cards=[];
  for(const opt of opts){const g=genOpt(opt,board,br,bs,fs,used,cards);if(!g)return null;for(const c of g)if(!used.has(c)&&!cards.includes(c)){cards.push(c);used.add(c);}}
  while(cards.length<gt){
    const av=FD.filter(c=>!used.has(c)&&!cards.includes(c));
    if(!av.length)return null;
    // Fill with cards that don't accidentally create stronger hands
    const safe = av.filter(c => {
      const v = RV[c[0]];
      const s = c[1];
      // Avoid pairing board cards (unless already needed)
      const boardVals = board.map(b => RV[b[0]]);
      if (boardVals.includes(v) && !opts.some(o => ["top_pair","two_pair","trips","set","top_set","middle_set","bottom_set","full_house"].includes(o))) return false;
      // Avoid flush suit cards (unless needed)
      if (s === fs && !opts.some(o => ["flush","nut_flush","flush_draw","nut_flush_draw","combo_draw"].includes(o))) return false;
      return true;
    });
    const pool = safe.length > 0 ? safe : av;
    const p=pool[Math.floor(Math.random()*pool.length)];
    cards.push(p);used.add(p);
  }
  return cards.slice(0,gt);
}

function genOpt(opt,board,br,bs,fs,used,ex) {
  const av=(r,s)=>{const c=VR[r]+s;return !used.has(c)&&!ex.includes(c)?c:null;};
  const anySuit=r=>{for(const s of shuf([...SUITS])){const c=av(r,s);if(c)return c;}return null;};
  const nonF=r=>{for(const s of shuf(SUITS.filter(x=>x!==fs))){const c=av(r,s);if(c)return c;}return av(r,fs);};
  switch(opt){
    case"top_pair":{const c=nonF(br[0]);return c?[c]:null;}
    case"middle_pair":{if(br.length<2)return null;const c=nonF(br[1]);return c?[c]:null;}
    case"bottom_pair":{if(br.length<3)return null;const c=nonF(br[2]);return c?[c]:null;}
    case"overpair":{const ov=br[0]+1;if(ov>14)return null;const ss=shuf([...SUITS]);const c1=av(ov,ss[0]),c2=av(ov,ss[1]);return c1&&c2?[c1,c2]:null;}
    case"two_pair":{const c1=nonF(br[0]),c2=nonF(br[1]);return c1&&c2?[c1,c2]:null;}
    case"trips":{const c=anySuit(br[0]);return c?[c]:null;}
    case"set":
    case"top_set":{const tr=br[0];const ss=shuf([...SUITS]).filter(s=>{const bs2=board.find(b=>RV[b[0]]===tr);return bs2?s!==bs2[1]:true;});const c1=av(tr,ss[0]),c2=ss.length>1?av(tr,ss[1]):null;return c1&&c2?[c1,c2]:null;}
    case"middle_set":{if(br.length<2)return null;const tr=br[1];const ss=shuf([...SUITS]).filter(s=>{const bs2=board.find(b=>RV[b[0]]===tr);return bs2?s!==bs2[1]:true;});const c1=av(tr,ss[0]),c2=ss.length>1?av(tr,ss[1]):null;return c1&&c2?[c1,c2]:null;}
    case"bottom_set":{if(br.length<3)return null;const tr=br[2];const ss=shuf([...SUITS]).filter(s=>{const bs2=board.find(b=>RV[b[0]]===tr);return bs2?s!==bs2[1]:true;});const c1=av(tr,ss[0]),c2=ss.length>1?av(tr,ss[1]):null;return c1&&c2?[c1,c2]:null;}
    case"straight":{const bv=[...br].sort((a,b)=>a-b);for(let l=2;l<=10;l++){const s5=[l,l+1,l+2,l+3,l+4],nd=s5.filter(v=>!bv.includes(v));if(nd.length===2){const c1=nonF(nd[0]),c2=nonF(nd[1]);if(c1&&c2)return[c1,c2];}}return null;}
    case"flush":{
      // Non-nut flush: do NOT use Ace
      const hr=shuf([13,12,11,10,9,8,7]),picks=[];
      for(const r of hr){const c=av(r,fs);if(c)picks.push(c);if(picks.length===2)break;}
      return picks.length===2?picks:null;
    }
    case"nut_flush":{const c1=av(14,fs);if(!c1)return null;for(const r of shuf([13,12,11,10,9])){const c2=av(r,fs);if(c2)return[c1,c2];}return null;}
    case"full_house":{
      const tr=br[0];
      const ss=shuf([...SUITS]).filter(s=>{const bs2=board.find(b=>RV[b[0]]===tr);return bs2?s!==bs2[1]:true;});
      const c1=av(tr,ss[0]),c2=ss.length>1?av(tr,ss[1]):null;
      if(!c1||!c2)return null;
      // Also need to pair a second board card
      const c3=anySuit(br[1]);
      return c3?[c1,c2,c3]:null;
    }
    case"quads":{
      const tr=br[0];
      const ss=SUITS.filter(s=>{const bs2=board.find(b=>RV[b[0]]===tr);return bs2?s!==bs2[1]:true;});
      const cards=ss.map(s=>av(tr,s)).filter(Boolean);
      return cards.length>=3?cards.slice(0,3):null;
    }
    case"oesd":{for(let t=0;t<20;t++){const r1=br[0]+(Math.random()>0.5?1:-3),r2=r1+1;if(r1>=2&&r1<=14&&r2>=2&&r2<=14){const c1=nonF(r1),c2=nonF(r2);if(c1&&c2)return[c1,c2];}}return null;}
    case"flush_draw":{
      // Non-nut FD: do NOT use Ace of flush suit
      const ranks=shuf([13,12,11,10,9,8,7,6]),picks=[];
      for(const r of ranks){const c=av(r,fs);if(c)picks.push(c);if(picks.length===2)break;}
      return picks.length===2?picks:null;
    }
    case"nut_flush_draw":{const c1=av(14,fs);if(!c1)return null;for(const r of shuf([13,12,11,10,9,8])){const c2=av(r,fs);if(c2)return[c1,c2];}return null;}
    case"gutshot":{const r1=br[0]+2;if(r1>14||r1<2)return null;const c=nonF(r1);return c?[c]:null;}
    case"wrap":{const m=br[1]||br[0];for(let att=0;att<20;att++){const wc=[];const offsets=shuf([-3,-2,-1,1,2,3].filter(d=>{const r=m+d;return r>=2&&r<=14&&!br.includes(r);}));for(const d of offsets){const r=m+d;const c=nonF(r);if(c&&!ex.includes(c))wc.push(c);if(wc.length>=3)break;}if(wc.length<2)continue;const cards=wc.slice(0,3);const testHole=[...ex,...cards];if(testHole.length>=2){const best=bestO(testHole,board);if(best&&best.r>=4)continue;}return cards;}return null;}
    case"combo_draw":{
      // FD (not NFD) + straight draw
      const c1=(()=>{for(const r of shuf([13,12,11,10,9])){const c=av(r,fs);if(c)return c;}return null;})();
      if(!c1)return null;
      const cv=br[0]+1;
      if(cv<=14&&cv>=2){const c2=nonF(cv);if(c2)return[c1,c2];}
      return[c1];
    }
    case"overcards":{const ov=br[0]+1;if(ov>14)return null;const c=nonF(ov)||anySuit(14);return c?[c]:null;}
    case"backdoor_sd":{
      // One card that could connect for backdoor straight
      const m=br[1]||br[0];
      const r=m+3;
      if(r>=2&&r<=14){const c=nonF(r);if(c)return[c];}
      const r2=m-3;
      if(r2>=2&&r2<=14){const c=nonF(r2);if(c)return[c];}
      return null;
    }
    case"backdoor_fd":{const nbs=SUITS.find(s=>!bs.includes(s))||SUITS[3];const c=av(Math.floor(Math.random()*5)+10,nbs);return c?[c]:null;}
    case"blocker":{
      // Nut flush blocker: Ace of a suit that has cards on board
      for(const s of shuf([...SUITS])){
        if(bs.includes(s)){const c=av(14,s);if(c)return[c];}
      }
      return null;
    }
    case"air":{return [];} // No specific cards needed
    default:return null;
  }
}

/* ═══ UI COMPONENTS ═══ */

function MiniCard({card,onRemove,size="md"}) {
  const t = useT();
  if(!card) return null; const suit=card[1];
  const sz={sm:{w:28,h:38,f:10,s:9},md:{w:40,h:56,f:14,s:13}}[size];
  return(
    <div onClick={onRemove} style={{width:sz.w,height:sz.h,borderRadius:6,border:`2px solid ${SC[suit]}33`,background:`linear-gradient(135deg,white,${SBG[suit]})`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:onRemove?"pointer":"default",boxShadow:"0 2px 6px rgba(0,0,0,0.1)",userSelect:"none",position:"relative",flexShrink:0}} title={onRemove?t.player.clickToRemove:""}>
      <span style={{fontSize:sz.f,fontWeight:700,color:SC[suit],lineHeight:1}}>{card[0]}</span>
      <span style={{fontSize:sz.s,color:SC[suit],lineHeight:1,marginTop:1}}>{SS[suit]}</span>
      {onRemove&&<div style={{position:"absolute",top:-4,right:-4,width:14,height:14,borderRadius:"50%",background:"#e74c3c",color:"white",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",opacity:0.8}}>×</div>}
    </div>
  );
}

function Empty({dark=true}) { return <div style={{width:40,height:56,borderRadius:6,border:dark?"2px dashed rgba(255,255,255,0.15)":"2px dashed rgba(0,0,0,0.15)",display:"flex",alignItems:"center",justifyContent:"center",color:dark?"#555":"#ccc",fontSize:18,flexShrink:0}}>?</div>; }

function Picker({used,onPick}) {
  return(<div style={{padding:"12px 0"}}>{RANKS.map(rank=>(<div key={rank} style={{display:"flex",gap:3,marginBottom:3,justifyContent:"center"}}>{SUITS.map(suit=>{const c=rank+suit,u=used.has(c);return(<button key={c} disabled={u} onClick={()=>onPick(c)} style={{width:46,height:32,border:u?"1px solid #ddd":`1.5px solid ${SC[suit]}55`,borderRadius:5,background:u?"#f0f0f0":`linear-gradient(135deg,white,${SBG[suit]})`,color:u?"#ccc":SC[suit],fontWeight:700,fontSize:12,cursor:u?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:2,opacity:u?0.4:1,fontFamily:"'JetBrains Mono',monospace"}}>{rank}<span style={{fontSize:11}}>{SS[suit]}</span></button>);})}</div>))}</div>);
}

function Btn({onClick,children,style:s={},disabled=false}) {
  return <button onClick={onClick} disabled={disabled} style={{padding:"6px 10px",border:"none",borderRadius:7,fontSize:11,fontWeight:600,cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.5:1,transition:"all 0.15s",...s}}>{children}</button>;
}

function PlayerPanel({index,cards,gameType,onChange,allUsed,pickerTarget,setPickerTarget,onRandom,boardCards}) {
  const t = useT();
  const lang = useContext(LangContext);
  const[txt,setTxt]=useState("");const max=gameType,open=pickerTarget===`p-${index}`,pUsed=new Set(cards),color=PC[index%PC.length];
  const handAnalysis = useMemo(() => analyzeHand(cards, boardCards, lang), [cards, boardCards, lang]);
  const submit=()=>{const p=parseText(txt).filter(c=>!allUsed.has(c)||pUsed.has(c));onChange(p.slice(0,max));setTxt("");};
  const pick=c=>{if(cards.length<max){const n=[...cards,c];onChange(n);if(n.length>=max)setPickerTarget(null);}};
  const placeholder = max === 4 ? t.player.inputPlaceholder4 : max === 5 ? t.player.inputPlaceholder5 : t.player.inputPlaceholder6;
  return(
    <div style={{background:"white",borderRadius:12,padding:16,border:`2px solid ${color}22`,boxShadow:`0 4px 16px ${color}11`}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
        <div style={{width:28,height:28,borderRadius:"50%",background:`linear-gradient(135deg,${color},${color}cc)`,color:"white",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{index+1}</div>
        <span style={{fontWeight:700,fontSize:14,color:"#2c3e50"}}>{t.player.title} {index+1}</span>
        {handAnalysis&&<span style={{fontSize:10,color:"#888",marginLeft:8,fontFamily:"'JetBrains Mono',monospace"}}>{handAnalysis}</span>}
        <span style={{fontSize:11,color:"#95a5a6",marginLeft:"auto",fontFamily:"'JetBrains Mono',monospace"}}>{cards.length}/{max}</span>
      </div>
      <div style={{display:"flex",gap:5,minHeight:56,marginBottom:10,flexWrap:"wrap"}}>
        {cards.map((c,i)=><MiniCard key={i} card={c} onRemove={()=>onChange(cards.filter((_,j)=>j!==i))}/>)}
        {Array.from({length:max-cards.length}).map((_,i)=><Empty key={`e${i}`} dark={false}/>)}
      </div>
      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
        <div style={{flex:1,display:"flex",gap:4,minWidth:160}}>
          <input type="text" placeholder={placeholder} value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} style={{flex:1,padding:"7px 10px",border:"1.5px solid #e0e0e0",borderRadius:7,fontSize:12,fontFamily:"'JetBrains Mono',monospace",outline:"none",minWidth:0}} onFocus={e=>e.target.style.borderColor=color} onBlur={e=>e.target.style.borderColor="#e0e0e0"}/>
          <Btn onClick={submit} style={{background:color,color:"white",fontSize:12}}>{t.player.input}</Btn>
        </div>
        <Btn onClick={()=>setPickerTarget(open?null:`p-${index}`)} style={{background:open?"#34495e":"#ecf0f1",color:open?"white":"#34495e",fontSize:12}}>{open?t.player.close:t.player.select}</Btn>
        <Btn onClick={onRandom} style={{background:"#f0f0f0",color:"#555",fontSize:12}} disabled={cards.length>=max}>🎲</Btn>
        <Btn onClick={()=>onChange([])} style={{background:"rgba(231,76,60,0.1)",color:"#e74c3c",fontSize:12}} disabled={cards.length===0}>✕</Btn>
      </div>
      {open&&<div style={{marginTop:10,borderTop:"1px solid #f0f0f0",paddingTop:8}}><Picker used={allUsed} onPick={pick}/></div>}
    </div>
  );
}

function Loading({method}) {
  const t = useT();
  return(<div style={{background:"rgba(15,12,41,0.85)",backdropFilter:"blur(6px)",borderRadius:14,padding:"28px 20px",border:"1px solid rgba(255,255,255,0.1)",display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
    <div style={{width:40,height:40,border:"3px solid rgba(102,126,234,0.25)",borderTop:"3px solid #667eea",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
    <span style={{color:"#c0c0e0",fontSize:14,fontWeight:600}}>{method==="exact"?t.calc.exactCalc:t.calc.mcCalc}</span>
  </div>);
}

function Results({results,playerCards,boardCards}) {
  const t = useT();
  if(!results) return null; const method=results[0]?.method,total=results[0]?.total;
  return(
    <div style={{background:"rgba(255,255,255,0.06)",backdropFilter:"blur(10px)",borderRadius:14,padding:20,border:"1px solid rgba(255,255,255,0.1)",animation:"fadeIn 0.3s ease"}}>
      <h3 style={{margin:"0 0 16px",fontSize:16,fontWeight:800,color:"#c0c0e0",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <span>{t.results.title}</span>
        {boardCards&&boardCards.length>0?(
          <div style={{display:"flex",gap:3,alignItems:"center"}}>
            {boardCards.map((c,i)=>(
              <div key={i} style={{width:24,height:32,borderRadius:4,border:`1.5px solid ${SC[c[1]]}44`,background:`linear-gradient(135deg,white,${SBG[c[1]]})`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:9,fontWeight:700,color:SC[c[1]],lineHeight:1}}>{c[0]}</span>
                <span style={{fontSize:8,color:SC[c[1]],lineHeight:1}}>{SS[c[1]]}</span>
              </div>
            ))}
          </div>
        ):(
          <span style={{fontSize:11,color:"#888",fontWeight:400}}>{t.settings.preflop}</span>
        )}
        {method==="exact"&&<span style={{fontSize:11,color:"#6bffb0",fontWeight:600}}>✓ {t.results.exact}</span>}
      </h3>
      <div style={{display:"grid",gap:10}}>
        {results.map((r,i)=>{const color=PC[i%PC.length],eq=parseFloat(r.equity);return(
          <div key={i} style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:14,border:`1px solid ${color}33`}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:color,color:"white",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:11,fontFamily:"'JetBrains Mono',monospace"}}>{i+1}</div>
                <span style={{fontWeight:700,fontSize:13,color:"#d0d0e0"}}>P{i+1}</span>
                <div style={{display:"flex",gap:3,marginLeft:4}}>{(playerCards[i]||[]).map((c,j)=><MiniCard key={j} card={c} size="sm"/>)}</div>
              </div>
              <span style={{fontSize:22,fontWeight:900,color,fontFamily:"'JetBrains Mono',monospace"}}>{r.equity}%</span>
            </div>
            <div style={{background:"rgba(255,255,255,0.06)",borderRadius:6,height:8,overflow:"hidden",marginBottom:6}}>
              <div style={{width:`${eq}%`,height:"100%",background:`linear-gradient(90deg,${color},${color}aa)`,borderRadius:6,transition:"width 0.5s ease"}}/>
            </div>
            <div style={{display:"flex",gap:16,fontSize:11,color:"#8888aa",fontFamily:"'JetBrains Mono',monospace"}}>
              <span>{t.results.win}: {r.win}%</span><span>{t.results.tie}: {r.tie}%</span><span>{t.results.equity}: {r.equity}%</span>
            </div>
          </div>);
        })}
      </div>
      <div style={{marginTop:14,fontSize:11,color:"#666",textAlign:"center",fontFamily:"'JetBrains Mono',monospace"}}>
        {method==="exact"?t.results.totalExact(total.toLocaleString()):t.results.totalMC(total.toLocaleString())}
      </div>
    </div>
  );
}

/* ═══ NEXT CARD ANALYSIS PANEL ═══ */
function NextCardPanel({ data, playerCards, boardLen, onCardClick }) {
  const t = useT();
  const [sortBy, setSortBy] = useState(0);
  const [viewMode, setViewMode] = useState("chart");
  const [suitFilter, setSuitFilter] = useState("all");

  if (!data || data.length === 0) return null;

  const label = boardLen === 3 ? t.board.turn : boardLen === 4 ? t.board.river : "";
  const numPlayers = data[0].equities.length;

  let filtered = suitFilter === "all" ? data : data.filter(d => d.card[1] === suitFilter);
  const sorted = [...filtered].sort((a, b) => b.equities[sortBy] - a.equities[sortBy]);

  const avgEq = data.reduce((s, d) => s + d.equities[sortBy], 0) / data.length;

  // Layout optimization: 1-column if <=25 cards or suit filter active, 2-column if >25
  const useDoubleColumn = suitFilter === "all" && sorted.length > 25;
  const barHeight = 32;

  // For double column, split sorted array into two halves (left top->bottom, right top->bottom)
  let leftCol, rightCol;
  if (useDoubleColumn) {
    const mid = Math.ceil(sorted.length / 2);
    leftCol = sorted.slice(0, mid);
    rightCol = sorted.slice(mid);
  }

  const renderBarItem = (item) => {
    const eq = item.equities[sortBy];
    const isGood = eq > avgEq + 5;
    const isBad = eq < avgEq - 5;
    return (
      <div key={item.card} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, padding: "2px 0" }}>
        <div onClick={() => onCardClick && onCardClick(item.card)}
          style={{ width: 34, height: 28, borderRadius: 4, border: `1.5px solid ${SC[item.card[1]]}44`,
          background: `linear-gradient(135deg,white,${SBG[item.card[1]]})`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0,
          cursor: onCardClick ? "pointer" : "default", transition: "transform 0.15s" }}
          onMouseEnter={e => onCardClick && (e.currentTarget.style.transform = "scale(1.1)")}
          onMouseLeave={e => onCardClick && (e.currentTarget.style.transform = "scale(1)")}>
          <span style={{ fontSize: 10, fontWeight: 700, color: SC[item.card[1]], lineHeight: 1 }}>{item.card[0]}</span>
          <span style={{ fontSize: 8, color: SC[item.card[1]], lineHeight: 1 }}>{SS[item.card[1]]}</span>
        </div>
        {/* Win-only bar for primary player */}
        <div style={{ flex: 1, position: "relative", height: 16, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${eq}%`, height: "100%",
            background: `linear-gradient(90deg, ${PC[sortBy]}, ${isGood ? "#2ecc71" : isBad ? "#e74c3c" : PC[sortBy]}aa)`,
            borderRadius: 4, transition: "width 0.3s ease" }} />
        </div>
        <div style={{ width: 46, textAlign: "right", flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace",
            color: isGood ? "#2ecc71" : isBad ? "#e74c3c" : "#ccc" }}>
            {eq.toFixed(1)}%
          </span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(10px)", borderRadius: 14,
      padding: 20, border: "1px solid rgba(255,255,255,0.1)", animation: "fadeIn 0.3s ease" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#c0c0e0" }}>
          {t.nextCard.title(label)}
        </h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 2, background: "rgba(255,255,255,0.06)", borderRadius: 8, padding: 2 }}>
            {[{id:"chart",label:t.nextCard.chart},{id:"grid",label:t.nextCard.grid}].map(m => (
              <button key={m.id} onClick={() => setViewMode(m.id)}
                style={{ padding: "4px 10px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: 600,
                  background: viewMode === m.id ? "rgba(102,126,234,0.3)" : "transparent",
                  color: viewMode === m.id ? "#a8b8ff" : "#888", cursor: "pointer" }}>{m.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Controls row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#888" }}>{t.nextCard.sortBy}:</span>
          <div style={{ display: "flex", gap: 3 }}>
            {Array.from({ length: numPlayers }).map((_, i) => (
              <button key={i} onClick={() => setSortBy(i)}
                style={{
                  padding: "3px 10px", borderRadius: 12, border: sortBy === i ? `2px solid ${PC[i]}` : "1px solid rgba(255,255,255,0.1)",
                  background: sortBy === i ? `${PC[i]}22` : "transparent",
                  color: sortBy === i ? PC[i] : "#888", fontSize: 11, fontWeight: 700, cursor: "pointer",
                }}>P{i + 1}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#888" }}>{t.nextCard.suit}:</span>
          <div style={{ display: "flex", gap: 3 }}>
            <button onClick={() => setSuitFilter("all")}
              style={{ padding: "3px 8px", borderRadius: 12, border: suitFilter === "all" ? "2px solid #667eea" : "1px solid rgba(255,255,255,0.1)",
                background: suitFilter === "all" ? "rgba(102,126,234,0.15)" : "transparent",
                color: suitFilter === "all" ? "#a8b8ff" : "#888", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.nextCard.all}</button>
            {SUITS.map(s => (
              <button key={s} onClick={() => setSuitFilter(s)}
                style={{ padding: "3px 8px", borderRadius: 12, border: suitFilter === s ? `2px solid ${SC[s]}` : "1px solid rgba(255,255,255,0.1)",
                  background: suitFilter === s ? `${SC[s]}22` : "transparent",
                  color: SC[s], fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{SS[s]}</button>
            ))}
          </div>
        </div>

        <span style={{ fontSize: 11, color: "#666", fontFamily: "'JetBrains Mono',monospace" }}>
          {filtered.length}{t.nextCard.cards}
        </span>
      </div>

      {/* CHART VIEW */}
      {viewMode === "chart" && (
        <div style={{ overflowY: "auto", maxHeight: 520, paddingRight: 4 }}>
          {useDoubleColumn ? (
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {leftCol.map(item => renderBarItem(item))}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {rightCol.map(item => renderBarItem(item))}
              </div>
            </div>
          ) : (
            sorted.map(item => renderBarItem(item))
          )}
        </div>
      )}

      {/* GRID VIEW */}
      {viewMode === "grid" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 6, maxHeight: 520, overflowY: "auto" }}>
          {sorted.map(item => {
            const eq = item.equities[sortBy];
            const isGood = eq > avgEq + 5;
            const isBad = eq < avgEq - 5;
            const bgColor = isGood ? "rgba(46,204,113,0.15)" : isBad ? "rgba(231,76,60,0.15)" : "rgba(255,255,255,0.04)";
            const borderColor = isGood ? "rgba(46,204,113,0.3)" : isBad ? "rgba(231,76,60,0.3)" : "rgba(255,255,255,0.08)";
            return (
              <div key={item.card} onClick={() => onCardClick && onCardClick(item.card)}
                style={{ background: bgColor, border: `1px solid ${borderColor}`,
                borderRadius: 8, padding: "8px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                cursor: onCardClick ? "pointer" : "default", transition: "transform 0.15s" }}
                onMouseEnter={e => onCardClick && (e.currentTarget.style.transform = "scale(1.05)")}
                onMouseLeave={e => onCardClick && (e.currentTarget.style.transform = "scale(1)")}>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: SC[item.card[1]] }}>{item.card[0]}</span>
                  <span style={{ fontSize: 12, color: SC[item.card[1]] }}>{SS[item.card[1]]}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace",
                  color: isGood ? "#2ecc71" : isBad ? "#e74c3c" : "#ccc" }}>
                  {eq.toFixed(1)}%
                </span>
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                  {item.equities.map((peq, pi) => (
                    <div key={pi} style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${peq}%`, height: "100%", background: PC[pi], borderRadius: 2 }} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary stats */}
      <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 10,
        display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
        {Array.from({ length: numPlayers }).map((_, pi) => {
          const eqs = data.map(d => d.equities[pi]);
          const best = Math.max(...eqs).toFixed(1);
          const worst = Math.min(...eqs).toFixed(1);
          const avg = (eqs.reduce((a, b) => a + b, 0) / eqs.length).toFixed(1);
          const goodCards = data.filter(d => d.equities[pi] > 60).length;
          return (
            <div key={pi} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: PC[pi], marginBottom: 4 }}>Player {pi + 1}</div>
              <div style={{ fontSize: 10, color: "#888", fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.6 }}>
                {t.nextCard.best}: <span style={{ color: "#2ecc71" }}>{best}%</span> · {t.nextCard.worst}: <span style={{ color: "#e74c3c" }}>{worst}%</span>
                <br />{t.nextCard.avg}: {avg}% · {t.nextCard.favorable}({">"}60%): <span style={{ color: "#6bffb0" }}>{goodCards}{t.nextCard.cards}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ SCENARIO BUILDER ═══ */
function ScenarioBuilder({onApply,gameType,open,setOpen}) {
  const t = useT();
  const lang = useContext(LangContext);
  const[tab,setTab]=useState("presets");
  const[p1s,setP1s]=useState([]),[p2s,setP2s]=useState([]);
  const[err,setErr]=useState(""),[gen,setGen]=useState(false);
  const[collapsed,setCollapsed]=useState({});
  const toggleCollapse=(key)=>setCollapsed(prev=>({...prev,[key]:!prev[key]}));
  const smartToggle=(sel,set,id)=>{
    if(sel.includes(id)){set(sel.filter(x=>x!==id));return;}
    let ns=[...sel];
    if(MADE_IDS.has(id)){
      // Remove all other made hands
      ns=ns.filter(x=>!MADE_IDS.has(x));
      // Remove incompatible draws (draw target <= new made strength)
      const mr=MADE_STRENGTH[id];
      ns=ns.filter(x=>{if(DRAW_IDS.has(x))return DRAW_PRODUCES[x]>mr;return true;});
      ns.push(id);
    } else if(DRAW_IDS.has(id)){
      const sm=ns.find(x=>MADE_IDS.has(x));
      if(sm&&DRAW_PRODUCES[id]<=MADE_STRENGTH[sm])return; // incompatible
      if(ns.length<3)ns.push(id);
    } else {
      if(ns.length<3)ns.push(id);
    }
    set(ns);
  };
  const isOptDisabled=(sel,id)=>{
    if(sel.includes(id))return false;
    if(DRAW_IDS.has(id)){
      const sm=sel.find(x=>MADE_IDS.has(x));
      if(sm&&DRAW_PRODUCES[id]<=MADE_STRENGTH[sm])return true;
    }
    return !MADE_IDS.has(id)&&!DRAW_IDS.has(id)?sel.length>=3:false;
  };
  const apply=(pr)=>{setGen(true);setErr("");setTimeout(()=>{const r=generateScenario(pr.p1,pr.p2,gameType);if(r){onApply(r);setErr("");}else setErr(t.scenario.failRetry);setGen(false);},50);};
  const custom=()=>{if(!p1s.length||!p2s.length){setErr(t.scenario.selectAtLeast);return;}setGen(true);setErr("");setTimeout(()=>{const r=generateScenario(p1s,p2s,gameType);if(r){onApply(r);setErr("");}else setErr(t.scenario.failHard);setGen(false);},50);};

  // ESC key handler
  useEffect(()=>{
    if(!open)return;
    const h=(e)=>{if(e.key==='Escape')setOpen(false);};
    window.addEventListener('keydown',h);
    return()=>window.removeEventListener('keydown',h);
  },[open,setOpen]);

  const handName = (id) => t.hands[id] || id;

  const categoryStyle = (bg) => ({
    padding: "8px 12px", marginBottom: 4, borderRadius: 8, background: bg,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    cursor: "pointer", userSelect: "none",
  });

  const renderOpts=(sel,setSel,label)=>(
    <div style={{marginBottom:18}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <div style={{width:24,height:24,borderRadius:"50%",background:PC[label==="P1"?0:1],color:"white",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:11}}>{label==="P1"?1:2}</div>
        <span style={{fontWeight:700,fontSize:13,color:"#d0d0e0"}}>{label}</span>
        <span style={{fontSize:11,color:"#666"}}>({sel.length}/3)</span>
      </div>
      {[
        {key:"madeHands",label:t.categories.madeHands,items:MADE_HANDS,bg:"rgba(46,204,113,0.08)"},
        {key:"draws",label:t.categories.draws,items:DRAWS,bg:"rgba(52,152,219,0.08)"},
        {key:"others",label:t.categories.others,items:EXTRAS,bg:"rgba(155,89,182,0.08)"},
      ].map(g=>{
        const colKey = `${label}_${g.key}`;
        const isCollapsed = collapsed[colKey];
        return(
        <div key={g.key} style={{marginBottom:6}}>
          <div style={categoryStyle(g.bg)} onClick={()=>toggleCollapse(colKey)}>
            <span style={{fontSize:11,color:"#bbb",fontWeight:700,letterSpacing:"0.05em"}}>{g.label}</span>
            <span style={{fontSize:10,color:"#666"}}>{isCollapsed?"▶":"▼"}</span>
          </div>
          {!isCollapsed && (
            <div style={{display:"flex",gap:4,flexWrap:"wrap",padding:"6px 4px"}}>
              {g.items.map(o=>{const dis=isOptDisabled(sel,o.id);return(
                <button key={o.id} onClick={()=>!dis&&smartToggle(sel,setSel,o.id)} disabled={dis}
                  style={{
                    padding:"5px 10px",borderRadius:6,fontSize:11,fontWeight:600,
                    border:sel.includes(o.id)?"2px solid #667eea":"1.5px solid rgba(255,255,255,0.1)",
                    background:sel.includes(o.id)?"rgba(102,126,234,0.2)":dis?"rgba(255,255,255,0.02)":"rgba(255,255,255,0.04)",
                    color:sel.includes(o.id)?"#a8b8ff":dis?"#555":"#999",
                    cursor:dis?"not-allowed":"pointer",opacity:dis?0.4:1,
                    transition:"all 0.15s",whiteSpace:"nowrap",
                  }}>
                  {handName(o.id)}
                </button>
              );})}
            </div>
          )}
        </div>
      )})}
    </div>
  );

  return(
    <div style={{position:"relative"}}>
      {/* Trigger button - always visible */}
      <button onClick={()=>setOpen(!open)} style={{width:"100%",padding:"14px 20px",borderRadius:14,background:open?"linear-gradient(135deg,#667eea,#764ba2)":"linear-gradient(135deg,rgba(102,126,234,0.15),rgba(118,75,162,0.15))",border:open?"none":"1.5px solid rgba(102,126,234,0.3)",color:open?"white":"#a8b8ff",fontSize:14,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all 0.2s ease"}}>
        {t.scenario.title}<span style={{fontSize:11,color:open?"rgba(255,255,255,0.7)":"#8888cc",fontWeight:400}}>{open?t.scenario.close:t.scenario.presetAndCustom}</span>
      </button>

      {/* Overlay modal */}
      {open&&(
        <>
          {/* Backdrop */}
          <div onClick={()=>setOpen(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",zIndex:9999,animation:"fadeIn 0.2s ease"}}/>
          {/* Modal */}
          <div style={{position:"absolute",top:"100%",left:0,right:0,marginTop:8,width:"100%",maxHeight:"70vh",overflowY:"auto",background:"rgba(15,12,41,0.98)",backdropFilter:"blur(20px)",borderRadius:14,border:"1px solid rgba(255,255,255,0.12)",zIndex:10000,animation:"fadeIn 0.2s ease",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
            {/* Header */}
            <div style={{padding:"16px 18px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:"rgba(15,12,41,0.98)",zIndex:1,borderRadius:"14px 14px 0 0"}}>
              <span style={{fontSize:15,fontWeight:800,color:"#c0c0e0"}}>{t.scenario.title}</span>
              <button onClick={()=>setOpen(false)} style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:8,padding:"6px 12px",color:"#888",fontSize:12,cursor:"pointer",fontWeight:600}}>{t.scenario.close}</button>
            </div>
            {/* Tabs */}
            <div style={{display:"flex",padding:"12px 18px 0",gap:8}}>
              {[{id:"presets",l:t.scenario.preset},{id:"custom",l:t.scenario.custom}].map(tb=>(
                <button key={tb.id} onClick={()=>setTab(tb.id)}
                  onMouseEnter={e=>{if(tab!==tb.id){e.currentTarget.style.background="rgba(255,255,255,0.18)";e.currentTarget.style.transform="translateY(-1px)";}}}
                  onMouseLeave={e=>{if(tab!==tb.id){e.currentTarget.style.background="rgba(255,255,255,0.1)";e.currentTarget.style.transform="translateY(0)";}}}
                  style={{
                    flex:1,padding:"10px 16px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:700,
                    border:tab===tb.id?"none":"1px solid rgba(255,255,255,0.2)",
                    background:tab===tb.id?"linear-gradient(135deg,#667eea,#764ba2)":"rgba(255,255,255,0.1)",
                    color:tab===tb.id?"white":"#aaa",
                    boxShadow:tab===tb.id?"0 4px 12px rgba(102,126,234,0.3)":"none",
                    transition:"all 0.2s ease",
                  }}>{tb.l}</button>
              ))}
            </div>
            {/* Content */}
            <div style={{padding:18}}>
              {tab==="presets"&&<div style={{display:"grid",gap:8}}>{PRESETS.map((pr,i)=>(
                <button key={i} onClick={()=>apply(pr)} disabled={gen} style={{padding:"12px 16px",borderRadius:10,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",color:"#c0c0e0",cursor:gen?"wait":"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,marginBottom:2}}>{PRESET_NAMES[lang]?.[pr.id] || pr.id}</div>
                    <div style={{fontSize:11,color:"#888"}}>P1: {pr.p1.map(id=>handName(id)).join("+")} vs P2: {pr.p2.map(id=>handName(id)).join("+")}</div>
                  </div>
                  <span style={{fontSize:18,opacity:0.4}}>→</span>
                </button>
              ))}</div>}
              {tab==="custom"&&<div>
                {renderOpts(p1s,setP1s,"P1")}
                <div style={{height:1,background:"rgba(255,255,255,0.06)",margin:"16px 0"}}/>
                {renderOpts(p2s,setP2s,"P2")}
                {(p1s.length>0||p2s.length>0)&&<div style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:12,marginBottom:12,border:"1px solid rgba(255,255,255,0.06)"}}>
                  <div style={{fontSize:12,color:"#aaa",marginBottom:4}}><strong style={{color:PC[0]}}>P1:</strong> {p1s.map(id=>handName(id)).join(" + ")||t.scenario.unselected}</div>
                  <div style={{fontSize:12,color:"#aaa"}}><strong style={{color:PC[1]}}>P2:</strong> {p2s.map(id=>handName(id)).join(" + ")||t.scenario.unselected}</div>
                </div>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={custom} disabled={gen||!p1s.length||!p2s.length} style={{flex:1,padding:"12px",borderRadius:10,border:"none",fontSize:14,fontWeight:700,background:p1s.length&&p2s.length?"linear-gradient(135deg,#667eea,#764ba2)":"rgba(255,255,255,0.08)",color:p1s.length&&p2s.length?"white":"#666",cursor:gen||!p1s.length||!p2s.length?"not-allowed":"pointer"}}>{gen?t.scenario.generating:t.scenario.generate}</button>
                  <button onClick={()=>{setP1s([]);setP2s([]);setErr("");}} style={{padding:"12px 16px",borderRadius:10,border:"1px solid rgba(255,255,255,0.1)",background:"transparent",color:"#888",fontSize:12,fontWeight:600,cursor:"pointer"}}>{t.scenario.reset}</button>
                </div>
              </div>}
              {err&&<div style={{marginTop:12,padding:"10px 14px",borderRadius:8,background:"rgba(231,76,60,0.15)",color:"#e74c3c",fontSize:12,fontWeight:600}}>{err}</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══ LANGUAGE TOGGLE ═══ */
function LangToggle({ lang, setLang }) {
  return (
    <div style={{ display: "flex", gap: 2, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: 2 }}>
      <button onClick={() => setLang("ko")}
        style={{
          padding: "4px 12px", borderRadius: 18, border: "none", fontSize: 12, fontWeight: 600,
          background: lang === "ko" ? "rgba(102,126,234,0.3)" : "transparent",
          color: lang === "ko" ? "#a8b8ff" : "#888", cursor: "pointer", transition: "all 0.15s",
        }}>한국어</button>
      <button onClick={() => setLang("en")}
        style={{
          padding: "4px 12px", borderRadius: 18, border: "none", fontSize: 12, fontWeight: 600,
          background: lang === "en" ? "rgba(102,126,234,0.3)" : "transparent",
          color: lang === "en" ? "#a8b8ff" : "#888", cursor: "pointer", transition: "all 0.15s",
        }}>English</button>
    </div>
  );
}

/* ═══ MAIN APP ═══ */
export default function App() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("plo-lang") || "ko"; } catch { return "ko"; }
  });
  const changeLang = (l) => { setLang(l); try { localStorage.setItem("plo-lang", l); } catch {} };

  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [gt, setGt] = useState(4);
  const [np, setNp] = useState(2);
  const [pc, setPc] = useState([[], []]);
  const [bc, setBc] = useState([]);
  const [bt, setBt] = useState("");
  const [res, setRes] = useState(null);
  const [calc, setCalc] = useState(false);
  const [cm, setCm] = useState("");
  const [pt, setPt] = useState(null);
  const [sc, setSc] = useState(30000);
  const [nextCardData, setNextCardData] = useState(null);
  const [nextCardCalc, setNextCardCalc] = useState(false);
  const timer = useRef(null);
  const ncTimer = useRef(null);

  const t = translations[lang];

  const allUsed = useMemo(() => {
    const s = new Set();
    for (const p of pc) for (const c of p) s.add(c);
    for (const c of bc) s.add(c);
    return s;
  }, [pc, bc]);

  const ap = pc.slice(0, np);
  const canCalc = ap.every(p => p.length === gt) && np >= 2;

  useEffect(() => {
    if (!canCalc || bc.length < 3) return;
    if (timer.current) clearTimeout(timer.current);
    setCalc(true);
    setCm(5 - bc.length <= 2 ? "exact" : "montecarlo");
    timer.current = setTimeout(() => {
      setRes(runCalc(ap, bc, sc));
      setCalc(false);
    }, 80);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [bc, canCalc, JSON.stringify(ap), sc]);

  useEffect(() => {
    if (!canCalc || bc.length < 3 || bc.length > 4) {
      setNextCardData(null);
      return;
    }
    if (ncTimer.current) clearTimeout(ncTimer.current);
    setNextCardCalc(true);
    ncTimer.current = setTimeout(() => {
      const data = calcNextCardEquities(ap, bc);
      setNextCardData(data);
      setNextCardCalc(false);
    }, 150);
    return () => { if (ncTimer.current) clearTimeout(ncTimer.current); };
  }, [bc, canCalc, JSON.stringify(ap)]);

  const chGt = n => { setGt(n); setPc(p => p.map(x => x.slice(0, n))); setRes(null); setNextCardData(null); };
  const chNp = n => { setNp(n); setPc(p => { const x = [...p]; while (x.length < n) x.push([]); return x.slice(0, n); }); setRes(null); setNextCardData(null); };
  const chPc = (i, cards) => { setPc(p => { const x = [...p]; x[i] = cards; return x; }); if (bc.length < 3) { setRes(null); setNextCardData(null); } };
  const chBc = b => { setBc(b); if (b.length < 3) { setRes(null); setNextCardData(null); } };
  const bSub = () => { const p = parseText(bt), bU = new Set(bc), v = p.filter(c => !allUsed.has(c) || bU.has(c)); chBc(v.slice(0, 5)); setBt(""); };
  const bPick = c => { if (bc.length < 5) { const n = [...bc, c]; chBc(n); if (n.length >= 5) setPt(null); } };
  const rFillP = i => { const cur = pc[i] || [], need = gt - cur.length; if (need <= 0) return; const av = FD.filter(c => !allUsed.has(c)); chPc(i, [...cur, ...shuf(av).slice(0, need)]); };
  const rFillB = tgt => { const av = FD.filter(c => !allUsed.has(c)), need = tgt - bc.length; if (need <= 0) return; chBc([...bc, ...shuf(av).slice(0, need)]); };
  const manCalc = () => { if (!canCalc) return; setCalc(true); setCm(5 - bc.length <= 2 ? "exact" : "montecarlo"); setTimeout(() => { setRes(runCalc(ap, bc, sc)); setCalc(false); }, 50); };
  const clearAll = () => { setPc(Array.from({ length: np }, () => [])); setBc([]); setRes(null); setPt(null); setNextCardData(null); };
  const bpo = pt === "board";

  const applyScenario = (scenario) => {
    setNp(2);
    setBc(scenario.board);
    setPc(prev => {
      const next = [...prev];
      next[0] = scenario.players[0];
      next[1] = scenario.players[1];
      while (next.length < 2) next.push([]);
      return next.slice(0, Math.max(np, 2));
    });
    setRes(null); setPt(null); setNextCardData(null);
  };

  const handleNextCardClick = (card) => {
    if (bc.length < 5 && !allUsed.has(card)) {
      chBc([...bc, card]);
    }
  };

  const gBtn = (label, sel, onClick, extra = {}) => (
    <button onClick={onClick} style={{
      padding: "8px 18px", background: sel ? "linear-gradient(135deg,#667eea,#764ba2)" : "rgba(255,255,255,0.08)",
      color: sel ? "white" : "#aaa", border: sel ? "none" : "1px solid rgba(255,255,255,0.1)",
      borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono',monospace", ...extra,
    }}>{label}</button>
  );

  return (
    <LangContext.Provider value={lang}>
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#0f0c29 0%,#1a1a3e 30%,#24243e 100%)",
      fontFamily: "'Noto Sans KR','Segoe UI',sans-serif", color: "#2c3e50" }}>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;600;700;800;900&family=JetBrains+Mono:wght@400;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ padding: "20px 20px 12px", textAlign: "center", position: "relative" }}>
        <div style={{ position: "absolute", top: 20, right: 20 }}>
          <LangToggle lang={lang} setLang={changeLang} />
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {["♠","♥","♦","♣"].map((s, i) => <span key={i} style={{ fontSize: 18, color: ["#a0a0c0","#ff6b6b","#6bc5ff","#6bffb0"][i], opacity: 0.9 }}>{s}</span>)}
          </div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, background: "linear-gradient(135deg,#f8f8ff,#c0c0e0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t.header.title}</h1>
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "#8888aa", letterSpacing: "0.05em" }}>{t.header.subtitle}</p>
      </div>

      <div className="main-container" style={{ maxWidth: 1600, margin: "0 auto", padding: "0 16px 40px" }}>

        <div className="responsive-layout">
          {/* LEFT COLUMN */}
          <div className="left-column">
            <div style={{ marginBottom: 14 }}>
              <ScenarioBuilder onApply={applyScenario} gameType={gt} open={scenarioOpen} setOpen={setScenarioOpen} />
            </div>

            {/* Settings */}
            <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(10px)", borderRadius: 14, padding: 16, marginBottom: 14, border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#8888aa", letterSpacing: "0.08em", marginBottom: 5, display: "block" }}>{t.settings.gameType}</label>
                  <div style={{ display: "flex", gap: 4 }}>{[4, 5, 6].map(n => gBtn(`PLO${n}`, gt === n, () => chGt(n)))}</div>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#8888aa", letterSpacing: "0.08em", marginBottom: 5, display: "block" }}>{t.settings.players}</label>
                  <div style={{ display: "flex", gap: 4 }}>{[2, 3, 4, 5, 6].map(n => gBtn(n, np === n, () => chNp(n), { width: 36, height: 34, padding: 0 }))}</div>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#8888aa", letterSpacing: "0.08em", marginBottom: 5, display: "block" }}>{t.settings.simulation} <span style={{ color: "#666", fontWeight: 400 }}>({t.settings.preflop})</span></label>
                  <div style={{ display: "flex", gap: 4 }}>{[10000, 30000, 100000].map(n => gBtn(n >= 1000 ? `${n / 1000}K` : n, sc === n, () => setSc(n), { fontSize: 11, padding: "8px 12px" }))}</div>
                </div>
              </div>
            </div>

            {/* Board */}
            <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(10px)", borderRadius: 14, padding: 16, marginBottom: 14, border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#c0c0e0" }}>{t.board.title}</span>
                <span style={{ fontSize: 11, color: "#666", fontFamily: "'JetBrains Mono',monospace" }}>
                  {bc.length}/5
                  {bc.length >= 3 && canCalc && <span style={{ color: "#6bffb0", marginLeft: 6 }}>{t.board.auto}</span>}
                </span>
              </div>
              <div style={{ display: "flex", gap: 5, minHeight: 56, marginBottom: 10, flexWrap: "wrap" }}>
                {bc.map((c, i) => <MiniCard key={i} card={c} onRemove={() => chBc(bc.filter((_, j) => j !== i))} />)}
                {Array.from({ length: 5 - bc.length }).map((_, i) => <Empty key={`e${i}`} />)}
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <div style={{ flex: 1, display: "flex", gap: 4, minWidth: 120 }}>
                  <input type="text" placeholder={t.board.inputPlaceholder} value={bt} onChange={e => setBt(e.target.value)} onKeyDown={e => e.key === "Enter" && bSub()}
                    style={{ flex: 1, padding: "7px 10px", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 7, fontSize: 12, fontFamily: "'JetBrains Mono',monospace", background: "rgba(255,255,255,0.06)", color: "#eee", outline: "none", minWidth: 0 }} />
                  <Btn onClick={bSub} style={{ background: "linear-gradient(135deg,#667eea,#764ba2)", color: "white", fontSize: 12 }}>{t.board.input}</Btn>
                </div>
                <Btn onClick={() => setPt(bpo ? null : "board")} style={{ background: bpo ? "#34495e" : "rgba(255,255,255,0.1)", color: bpo ? "white" : "#ccc", fontSize: 12 }}>{bpo ? t.board.close : t.board.select}</Btn>
                <Btn onClick={() => rFillB(3)} style={{ background: "rgba(255,255,255,0.08)", color: "#aaa", fontSize: 11 }} disabled={bc.length >= 3}>🎲 {t.board.flop}</Btn>
                <Btn onClick={() => rFillB(4)} style={{ background: "rgba(255,255,255,0.08)", color: "#aaa", fontSize: 11 }} disabled={bc.length >= 4}>🎲 {t.board.turn}</Btn>
                <Btn onClick={() => rFillB(5)} style={{ background: "rgba(255,255,255,0.08)", color: "#aaa", fontSize: 11 }} disabled={bc.length >= 5}>🎲 {t.board.river}</Btn>
                <Btn onClick={() => chBc([])} style={{ background: "rgba(231,76,60,0.15)", color: "#e74c3c", fontSize: 12 }} disabled={bc.length === 0}>{t.board.clear}</Btn>
              </div>
              {bpo && <div style={{ marginTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 8 }}><Picker used={allUsed} onPick={bPick} /></div>}
            </div>

            {/* Players */}
            <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
              {Array.from({ length: np }).map((_, i) =>
                <PlayerPanel key={i} index={i} cards={pc[i] || []} gameType={gt} onChange={c => chPc(i, c)} allUsed={allUsed} pickerTarget={pt} setPickerTarget={setPt} onRandom={() => rFillP(i)} boardCards={bc} />)}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <button onClick={manCalc} disabled={!canCalc || calc}
                style={{ flex: 1, padding: "13px 24px", background: canCalc && !calc ? "linear-gradient(135deg,#667eea,#764ba2)" : "rgba(255,255,255,0.08)",
                  color: canCalc && !calc ? "white" : "#666", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800,
                  cursor: canCalc && !calc ? "pointer" : "not-allowed", boxShadow: canCalc ? "0 4px 20px rgba(102,126,234,0.3)" : "none" }}>
                {calc ? t.calc.calculating : bc.length >= 3 ? t.calc.recalc : t.calc.calcEquity}
              </button>
              <button onClick={clearAll} style={{ padding: "13px 18px", background: "rgba(231,76,60,0.15)", color: "#e74c3c",
                border: "1px solid rgba(231,76,60,0.2)", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t.calc.clearAll}</button>
            </div>

            {/* Help */}
            <div style={{ padding: 14, background: "rgba(255,255,255,0.03)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)" }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, color: "#8888aa" }}>{t.usage.title}</h4>
              <div style={{ fontSize: 11, color: "#666", lineHeight: 1.8 }}>
                <p style={{ margin: "0 0 4px" }}><strong style={{ color: "#9999bb" }}>{t.usage.scenario}</strong> {t.usage.scenarioDesc}</p>
                <p style={{ margin: "0 0 4px" }}><strong style={{ color: "#9999bb" }}>{t.usage.input}</strong> {t.usage.inputDesc}</p>
                <p style={{ margin: "0 0 4px" }}><strong style={{ color: "#9999bb" }}>{t.usage.autoCalc}</strong> {t.usage.autoCalcDesc}</p>
                <p style={{ margin: "0 0 4px" }}><strong style={{ color: "#6bffb0" }}>{t.usage.cardClick}</strong> {t.usage.cardClickDesc}</p>
                <p style={{ margin: "0 0 4px" }}><strong style={{ color: "#6bffb0" }}>{t.usage.flopTurnRiver}</strong> {t.usage.exactDesc} · <strong style={{ color: "#ff6b6b" }}>{t.usage.preflopLabel}</strong> {t.usage.mcDesc}</p>
                <p style={{ margin: 0 }}><strong style={{ color: "#9999bb" }}>{t.usage.notation}</strong> {t.usage.notationDesc}</p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="right-column">
            {calc && <Loading method={cm} />}
            {!calc && res && <Results results={res} playerCards={pc} boardCards={bc} />}

            {nextCardCalc && (
              <div style={{ marginTop: res ? 16 : 0 }}>
                <Loading method="exact" />
              </div>
            )}
            {!nextCardCalc && nextCardData && (
              <div style={{ marginTop: res ? 16 : 0 }}>
                <NextCardPanel data={nextCardData} playerCards={pc} boardLen={bc.length} onCardClick={handleNextCardClick} />
              </div>
            )}

            {!calc && !res && !nextCardCalc && !nextCardData && (
              <div style={{ background: "rgba(255,255,255,0.04)", backdropFilter: "blur(10px)", borderRadius: 14,
                padding: "60px 20px", border: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📊</div>
                <p style={{ color: "#888", fontSize: 14, margin: 0, whiteSpace: "pre-line" }}>{t.emptyState}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        *{box-sizing:border-box}
        input::placeholder{color:#888}
        ::-webkit-scrollbar{width:6px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:3px}

        .responsive-layout {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .left-column {
          flex: 1;
          min-width: 0;
        }

        .right-column {
          flex: 1;
          min-width: 0;
          position: sticky;
          top: 20px;
        }

        @media (max-width: 1023px) {
          .responsive-layout {
            flex-direction: column;
          }

          .right-column {
            position: static;
            width: 100%;
          }
        }

        @media (min-width: 1024px) {
          .left-column {
            max-width: 50%;
          }

          .right-column {
            max-width: 50%;
          }
        }

        @media (min-width: 1400px) {
          .left-column {
            max-width: 600px;
          }

          .right-column {
            flex: 1;
            max-width: none;
          }
        }
      `}</style>
    </div>
    </LangContext.Provider>
  );
}
